import { Link, useNavigate } from "react-router-dom";
import { ShoppingBag, Package, Shield, LogOut, LogIn, ChevronDown, Menu, Store, FlaskConical, Calculator, Boxes, Newspaper } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const NAV_LINKS = [
  { to: "/shop", label: "Shop", testid: "nav-shop-link", icon: Store },
  { to: "/lab-results", label: "Lab Results", testid: "nav-lab-results-link", icon: FlaskConical },
  { to: "/research", label: "Research", testid: "nav-research-link", icon: Newspaper },
  { to: "/calculator", label: "Calculator", testid: "nav-calculator-link", icon: Calculator },
];

export const Navbar = () => {
  const { user, logout } = useAuth();
  const { count, bundlesVisible } = useCart();
  const navigate = useNavigate();

  const navLinks = bundlesVisible
    ? [NAV_LINKS[0], { to: "/bundles", label: "Bundles", testid: "nav-bundles-link", icon: Boxes }, ...NAV_LINKS.slice(1)]
    : NAV_LINKS;

  return (
    <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#F7F5F2]/80 border-b border-[#DDD7CF]/50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <div className="flex items-center gap-1 sm:gap-3 min-w-0">
          {/* Mobile menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button data-testid="nav-mobile-menu" className="md:hidden p-3 -ml-2 hover:bg-[#EAE5DE] rounded-full transition-colors" aria-label="Menu">
                <Menu className="w-5 h-5 text-[#1A1A1A]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-56">
              {navLinks.map(({ to, label, testid, icon: Icon }) => (
                <DropdownMenuItem key={to} data-testid={`mobile-${testid}`} onClick={() => navigate(to)} className="py-3">
                  <Icon className="w-4 h-4 mr-2" /> {label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="mobile-nav-cart-link" onClick={() => navigate("/cart")} className="py-3">
                <ShoppingBag className="w-4 h-4 mr-2" /> Cart{count > 0 ? ` (${count})` : ""}
              </DropdownMenuItem>
              {user ? (
                <DropdownMenuItem data-testid="mobile-nav-my-orders-link" onClick={() => navigate("/my-orders")} className="py-3">
                  <Package className="w-4 h-4 mr-2" /> My Orders
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem data-testid="mobile-nav-signin-link" onClick={() => navigate("/login")} className="py-3">
                  <LogIn className="w-4 h-4 mr-2" /> Sign In
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group shrink-0">
            <img
              src="/eb-logo.png"
              alt="Eternal Biolabs"
              className="h-9 w-auto transition-transform duration-500 group-hover:scale-105"
            />
            <span className="flex flex-col leading-none">
              <span className="font-display text-sm sm:text-base tracking-[0.25em] text-[#1A1A1A]">ETERNAL</span>
              <span className="text-[9px] tracking-[0.5em] text-[#8C7B6B] font-semibold mt-1">BIOLABS</span>
            </span>
          </Link>
        </div>

        <nav className="flex items-center gap-1 sm:gap-2">
          <div className="hidden md:flex items-center">
            {navLinks.map(({ to, label, testid }) => (
              <Link
                key={to}
                to={to}
                data-testid={testid}
                className="text-xs tracking-[0.2em] uppercase font-semibold text-[#6E675F] hover:text-[#1A1A1A] transition-colors px-3 py-2"
              >
                {label}
              </Link>
            ))}
          </div>
          <Link to="/cart" data-testid="nav-cart-link" className="relative p-3 hover:bg-[#EAE5DE] rounded-full transition-colors">
            <ShoppingBag className="w-5 h-5 text-[#1A1A1A]" />
            {count > 0 && (
              <span
                data-testid="nav-cart-count"
                className="absolute -top-0.5 -right-0.5 w-5 h-5 bg-[#8C7B6B] text-[#1A1A1A] text-[10px] font-bold rounded-full flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>

          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-none hover:bg-[#EAE5DE] transition-colors">
                  {user.picture ? (
                    <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" referrerPolicy="no-referrer" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-[#8C7B6B] flex items-center justify-center text-xs font-bold text-white">
                      {user.name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <ChevronDown className="w-4 h-4 text-[#6E675F]" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                <div className="px-3 py-2">
                  <p className="text-sm font-medium text-[#1A1A1A] truncate">{user.name}</p>
                  <p className="text-xs text-[#6E675F] truncate">{user.email}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-my-orders-link" onClick={() => navigate("/my-orders")}>
                  <Package className="w-4 h-4 mr-2" /> My Orders
                </DropdownMenuItem>
                {user.is_admin && (
                  <DropdownMenuItem data-testid="nav-admin-link" onClick={() => navigate("/admin")}>
                    <Shield className="w-4 h-4 mr-2" /> Admin Panel
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-logout-button" onClick={logout}>
                  <LogOut className="w-4 h-4 mr-2" /> Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <button
              data-testid="nav-signin-button"
              onClick={() => navigate("/login")}
              className="bg-[#8C7B6B] text-[#1A1A1A] text-xs tracking-[0.15em] uppercase font-semibold px-4 sm:px-5 py-3 sm:py-2.5 rounded-none hover:bg-[#75665A] hover:-translate-y-0.5 transition-transform duration-300 whitespace-nowrap"
            >
              Sign In
            </button>
          )}
        </nav>
      </div>
    </header>
  );
};
